import { Feature, Point } from "geojson";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { Layer, Source } from "react-map-gl";
import { useDrawMapStore, useMapStore } from "../..";
import useMapIcon from "../../hooks/useMapIcon";
import useNavigateToSaleComp from "../../hooks/useNavigateToSaleComp";
import useSubscribeMapMouseEvent from "../../hooks/useSubscribeMapMouseEvent";
import useRadiusSaleCompsOnMap from "../../hooks/useRadiusSaleCompsOnMap";
import useVisibleCompaniesSaleCompsOnMap from "../../hooks/useVisibleCompaniesSaleCompsOnMap";
import useVisibleSaleCompsOnMap from "../../hooks/useVisibleSaleCompsOnMap";
import ILayerProps from "../../interfaces/IlayerProps";
import { minZoom } from "../mapConfig";

export const saleCompMarkerLayerId = "saleCompMarkerLayer";

function SaleCompsMapLayer({ visible }: ILayerProps) {
    const navigateToSaleComp = useNavigateToSaleComp();
    const mapStore = useMapStore();
    const drawMapStore = useDrawMapStore();
    const saleComps = useVisibleSaleCompsOnMap();
    const companiesSaleComps = useVisibleCompaniesSaleCompsOnMap();
    const radiusSaleComps = useRadiusSaleCompsOnMap();

    useMapIcon({ name: "sale-comp-marker", url: "./sale-comp.png" });
    useSubscribeMapMouseEvent(() =>
        mapStore.onMouseClick("salecomps", feature => {
            if (feature?.layer?.id === saleCompMarkerLayerId && !drawMapStore.isActiveDrawing()) {
                navigateToSaleComp(Number(feature.id));
            }
        })
    );

    const saleCompMarkersGeoJson: any = useMemo(() => {
        const features = new Map<number, Feature<Point>>();

        if (visible) {
            for (const saleComp of saleComps) {
                features.set(saleComp.id, {
                    type: "Feature",
                    id: saleComp.id,
                    geometry: {
                        type: "Point",
                        coordinates: [saleComp.centerPoint.longitude, saleComp.centerPoint.latitude],
                    },
                    properties: {
                        icon: "sale-comp-marker",
                        minZoom: minZoom.saleComps,
                    },
                });
            }
        }

        for (const companiesSaleComp of companiesSaleComps) {
            features.set(companiesSaleComp.id, {
                type: "Feature",
                id: companiesSaleComp.id,
                geometry: {
                    type: "Point",
                    coordinates: [companiesSaleComp.centerPoint.longitude, companiesSaleComp.centerPoint.latitude],
                },
                properties: {
                    icon: companiesSaleComp.icon,
                    minZoom: minZoom.locations,
                },
            });
        }

        for (const radiusSaleComp of radiusSaleComps) {
            features.set(radiusSaleComp.id, {
                type: "Feature",
                id: radiusSaleComp.id,
                geometry: {
                    type: "Point",
                    coordinates: [radiusSaleComp.centerPoint.longitude, radiusSaleComp.centerPoint.latitude],
                },
                properties: {
                    icon: radiusSaleComp.icon,
                    minZoom: minZoom.locations,
                },
            });
        }

        return { type: "FeatureCollection", features: Array.from(features.values()) };
    }, [saleComps, companiesSaleComps, radiusSaleComps, visible]);

    if (!mapStore.isStyleLoaded)
        return null;

    return (
        <Source id="saleCompMarkerSource" type="geojson" data={saleCompMarkersGeoJson}>
            <Layer
                id={saleCompMarkerLayerId}
                type="symbol"
                filter={["<=", ["get", "minZoom"], ["zoom"]]}
                layout={{
                    "icon-allow-overlap": true,
                    "symbol-z-order": "source",
                    "icon-image": ["get", "icon"],
                }}
            />
        </Source>
    );
}

export default observer(SaleCompsMapLayer);